import styled from "@emotion/styled";
import { Stack, Typography } from "@mui/material";
import { DateField, DatePicker, type DateView } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import type { BaseDateValidationProps } from "@mui/x-date-pickers/internals";
import { ReactComponent as CalendarIcon } from "Components/_Shared/Icons/Assets/calendar.svg";
import { datePickerFieldStyles } from "Components/_Shared/Inputs/DatePickers/Styles";
import { StyledFormInput as BaseStyledFormInput, StyledFormControl, StyledValidationText } from "Forms/Components/FormStyles";
import { useFieldError } from "Forms/Hooks/useFieldError";
import { useDateFnsLocale } from "Infrastructure/Hooks/DateFns/useDateFnsLocale";
import { Spacing } from "Infrastructure/Themes/Themes";
import type { ExtractProps } from "Infrastructure/Utils/ObjectUtils";
import { format, fromUnixTime, isValid, parseISO } from "date-fns";
import { get } from "lodash-es";
import type { JSX } from "react";
import { type Control, Controller, type FieldErrors, type FieldPath, type FieldValues } from "react-hook-form";

const StyledLabel = styled.label<{ $disabled: boolean }>`
  color: ${(props) => (props.$disabled ? props.theme.palette.text.disabled : props.theme.palette.text.primary)};
`;

type NewType<FormType extends FieldValues> = {
  control: Control<FormType, object>;
  label?: string | JSX.Element;
  name: FieldPath<FormType>;
  errors: FieldErrors<FormType>;
  disablePast?: boolean;
  disableFuture?: boolean;
  formControlClass?: "long" | "medium" | "tiny";
  disabled?: boolean;
  readOnlyField?: boolean;
  onChange?: (date: Date | null) => void;
  labelEndAdornment?: JSX.Element;
  views?: DateView[];
};

export type Props<FormType extends FieldValues> = NewType<FormType> & BaseDateValidationProps<Date>;

const StyledFormInput = styled(BaseStyledFormInput)<{
  $hasError: boolean;
}>`
  .MuiInputBase-formControl {
    background-color: ${(props) => props.theme.palette.common.white};
  }

  .MuiInputBase-root {
    border: 1px solid ${(props) => (props.$hasError ? props.theme.palette.error.main : props.theme.colors.border)};
    border-radius: ${(props) => props.theme.spacing(Spacing.M)} !important;
    padding: ${(props) => props.theme.spacing(Spacing.M, Spacing.L)} !important;
    height: 40px;
  }

  .MuiInputBase-input {
    padding: 0px;
    box-sizing: border-box;
    color: ${(props) => props.theme.colors.textPrimary};
    font-size: 16px;
    font-weight: 400;
    line-height: 1;

    ::placeholder {
      color: ${(props) => props.theme.colors.textPrimary} !important;
    }
  }

  .MuiOutlinedInput-multiline {
    padding: 0;
  }

  .MuiOutlinedInput-notchedOutline {
    border: none;
  }

  .Mui-disabled {
    &.MuiInputBase-formControl {
      background-color: ${(props) => props.theme.colors.primaryDisabled};

      .MuiInputBase-input {
        color: ${(props) => props.theme.palette.text.primary}40;
      }
    }
  }
`;

const StyledDatePicker = styled(DatePicker)`
  & .Mui-selected, & .Mui-selected:hover, & .Mui-selected:focus {
    background-color: ${(props) => props.theme.colors.primaryHover} !important;
  }

  & input::selection {
    background-color: transparent;
  }
`;

const StyledDateField = styled(DateField)`
  ${() => {
    return datePickerFieldStyles;
  }}
`;

type DatePickerProps = ExtractProps<typeof DateField>;
type FieldProps = DatePickerProps & { readOnly?: boolean };

const Field: React.FunctionComponent<FieldProps> = (props) => {
  const { readOnly, ...rest } = props;
  return <StyledDateField readOnly={readOnly} {...rest} />;
};

export const BlDatePicker = <T extends object>(props: Props<T>) => {
  const {
    label,
    name,
    errors,
    disablePast,
    disableFuture,
    formControlClass,
    disabled,
    readOnlyField,
    control,
    labelEndAdornment,
    views,
    ...rest
  } = props;

  const fieldError = get(errors, name as string);
  const { hasError, errorMessage } = useFieldError({ fieldError });
  const locale = useDateFnsLocale();

  const handleChange = (value: Date | null, onChange: (...event: any[]) => void) => {
    if (isValid(value) && value) {
      const updatedValue = value;
      if (views && !views.includes("day")) {
        updatedValue.setDate(1);
      }
      onChange(format(value, "yyyy-MM-dd"));
      rest.onChange?.(value);
    }
  };

  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { onChange, value } }) => {
        return (
          <StyledFormControl className={`${formControlClass ?? ""} ${!label ? "no-label" : ""}`}>
            {!!label && (
              <StyledLabel htmlFor={name} $disabled={disabled ?? false}>
                <Stack direction={"row"} gap={1}>
                  <Typography fontWeight={500} variant="body2" mb={Spacing.S}>
                    {label}
                  </Typography>
                  {labelEndAdornment}
                </Stack>
              </StyledLabel>
            )}
            <StyledFormInput $hasError={hasError}>
              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={locale}>
                <StyledDatePicker
                  views={views}
                  format="dd. MM. yyyy"
                  value={typeof value === "string" ? (isValid(parseISO(value)) ? parseISO(value) : null) : isValid(value) ? value : null}
                  onYearChange={(value) => handleChange(value, onChange)}
                  onMonthChange={(value) => handleChange(value, onChange)}
                  onChange={(value) => handleChange(value, onChange)}
                  disablePast={disablePast}
                  disabled={disabled}
                  slots={{
                    field: Field,
                    openPickerIcon: CalendarIcon,
                  }}
                  slotProps={{
                    day: {
                      onClick: (e: any) => {
                        handleChange(fromUnixTime(e.target.dataset.timestamp / 1000), onChange);
                      },
                    },
                    field: { readOnly: true },
                  }}
                  disableFuture={disableFuture}
                  {...rest}
                />
              </LocalizationProvider>
            </StyledFormInput>
            {hasError && <StyledValidationText>{errorMessage}</StyledValidationText>}
          </StyledFormControl>
        );
      }}
    />
  );
};
